// Area membership is geometric and advisory: it never overrides a trip's phase, which
// stays detection-driven. These helpers are the single place the rest of the app asks
// "where is this vehicle standing right now".

export const PLANT_COLOR = '#2e7d46';
export const PARKING_COLOR = '#b26a00';
export const areaColor = (area) => (area.type === 'PARKING' ? PARKING_COLOR : PLANT_COLOR);
/** The colour each kind of area is drawn in, so a warehouse is never mistaken for a plant. */
export const AREA_COLOUR = { PARKING: '#b26a00', WAREHOUSE: '#2a6f97', PLANT: '#2e7d46' };

export const areaTypeLabel = (type) => (
  type === 'PARKING' ? 'Outside parking'
    : type === 'WAREHOUSE' ? 'Warehouse'
      : 'Plant area');

const occupantOf = (area, vehicleId) => (area.occupants || []).find((o) => o.vehicleId === vehicleId);

/** Areas a vehicle is currently standing in. A vehicle can be in more than one. */
export const areasOf = (areas, vehicleId) => (areas || []).filter((a) => occupantOf(a, vehicleId));

/** Human label for a vehicle's current location, or null when it is not in any area. */
export const areaNamesOf = (areas, vehicleId) => {
  const found = areasOf(areas, vehicleId);
  return found.length ? found.map((a) => a.name).join(', ') : null;
};

/** Milliseconds since the vehicle entered this area, or null if the crossing is unknown. */
export const waitingMs = (area, vehicleId) => {
  const occupant = occupantOf(area, vehicleId);
  if (!occupant?.enteredAt) return null;
  const ms = Date.now() - Date.parse(occupant.enteredAt);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
};

export const isStale = (area, vehicleId) => !!occupantOf(area, vehicleId)?.stale;

export const humanMs = (ms) => {
  if (ms == null) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  return hours < 48 ? `${hours} h ${mins % 60} min` : `${Math.floor(hours / 24)} d`;
};

/**
 * Vehicles parked in an outside-parking area, longest wait first — the call-in queue.
 * `withTrip` marks the ones that already have an open trip, so the gate can tell a
 * truck that is waiting to be processed from one that is simply staged.
 */
export const callInQueue = (areas, trips, vehicles) => (areas || [])
  .filter((a) => a.type === 'PARKING')
  .flatMap((area) => (area.occupants || []).map((o) => ({
    vehicleId: o.vehicleId,
    vehicle: (vehicles || []).find((v) => v.id === o.vehicleId)?.name || `Vehicle ${o.vehicleId}`,
    area: area.name,
    stale: !!o.stale,
    waitingMs: waitingMs(area, o.vehicleId),
    trip: (trips || []).find((t) => t.vehicleId === o.vehicleId && !t.closedAt) || null,
  })))
  .sort((a, b) => (b.waitingMs ?? -1) - (a.waitingMs ?? -1));
